import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
   Alert as RSAlert,
   Button, Col, Form, FormGroup,
   Input, InputGroup, Label, Row
} from "reactstrap";
import Logo from "../assets/images/logo.png";
import { DesktopContainer } from "../layouts";
import { api, storage, UserContext } from "../utils";
import { ServiceThreadSpinner } from "./";
import useLoginDeviceDetect from "../hooks/useLoginDeviceDetect";
import axios from "axios";

// Public/Authenticate's redirectUrl for an account on the authenticator-app method.
const TOTP_REDIRECT = {
   SIGN_IN: "#TOTP",                  // already set up - ask for the app's code
   ENROLL_EMAIL: "#TOTPENROLLEMAIL",  // not set up yet - a setup code was just emailed
};

// The authenticator-app steps, in the order a first-time user meets them. Each one posts its own
// endpoint with the current ticket and a code.
const TOTP_STEP = {
   SIGN_IN_CODE: "signInCode",
   EMAIL_CODE: "emailCode",
   SETUP: "setup",
};

const TOTP_ENDPOINTS = {
   [TOTP_STEP.SIGN_IN_CODE]: "Public/AuthenticateTotp",
   [TOTP_STEP.EMAIL_CODE]: "Public/VerifyTotpSetupCode",
   [TOTP_STEP.SETUP]: "Public/ConfirmTotpSetup",
};

const TOTP_CODE_LENGTH = 6;
const TOTP_QR_SIZE_PX = 200;
const TOTP_FAILURE_MESSAGE = "Unable to verify the code. Please try again.";
const TOTP_RESEND_FAILURE_MESSAGE = "Unable to send a new code. Please try again.";

// People paste codes as "123 456"; the server wants the digits only.
const stripWhitespace = (value) => (value || "").replace(/\s/g, "");

export default function SignIn(props) {
   const forgotPasswordPath = "/forgot_password";
   const scanLoginPath = "/scan_login";
   const badgeTokenPrefix = "B1-"; // opaque badge-token scheme; mirrors AppConstants.BadgeToken.SCHEME_PREFIX_V1
   const userCtx = useContext(UserContext);
   const navigate = useNavigate();
   const [authenticating, setAuthenticating] = useState(false);
   const [message, setMessage] = useState(null);
   const [password, setPassword] = useState("");
   const [showPassword, setShowPassword] = useState(false);
   // Authenticator-app sign-in. The ticket is held in component state only (never storage) and is
   // replaced by every response that carries a new one.
   const [totpStep, setTotpStep] = useState(null);
   const [totpTicket, setTotpTicket] = useState(null);
   const [totpCode, setTotpCode] = useState("");
   const [totpEmail, setTotpEmail] = useState("");       // masked address the setup code went to
   const [totpMinutes, setTotpMinutes] = useState(null); // the emailed setup code's lifetime, as the server states it
   const [totpNotice, setTotpNotice] = useState(null);   // the server's own line when no new code was sent (one went out moments ago)
   const [totpSetup, setTotpSetup] = useState(null);     // { qrSvg, setupKey } once the emailed code is accepted
   const [username, setUserName] = useState(storage.getItem("lastUsername") || "");
   const hasScanner = useLoginDeviceDetect(userCtx); // auto-detect scanner handheld; surfaces the badge login link

   useEffect(() => {
      let isMounted = true; // introduce a flag to track mount status
      const cancelToken = axios.CancelToken;
      const source = cancelToken.source();
      const queryStringParams = resolveQueryStringParams();
      // console.log('querystring is', queryStringParams)
      if (queryStringParams.state && queryStringParams.code) {
         setAuthenticating(true)
         api.postS("Public/Authenticate2fa", {
            state: queryStringParams.state,
            code: queryStringParams.code,
            username: username
         }, source.token)
            .then(response => {
               if (isMounted) { // only proceed if the component is still mounted
                  if (response.data.success) {
                     userCtx.signIn(response.data.user, response.data.user.token, response.data.menus);
                  }
                  else {
                     setAuthenticating(false);
                     setMessage({ flavor: "alert-danger", text: response?.data?.message });
                  }
               }
            })
            .catch((err) => {
               console.error(err);
               setAuthenticating(false);
            });
      }
      return () => {
         source.cancel("axios request cancelled");
         isMounted = false; // set isMounted to false when the component unmounts
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   // One submit for all three authenticator-app steps; the step decides the endpoint. The emailed-code
   // step moves on to setup, the other two end in a sign-in.
   function attemptTotpCode(evt) {
      if (evt) {
         evt.preventDefault();
      }
      if (authenticating) return;
      const code = stripWhitespace(totpCode);
      if (code.length < TOTP_CODE_LENGTH) {
         setMessage({ flavor: "alert-danger", text: "Please enter the 6-digit code." });
         return;
      }
      const step = totpStep;
      setAuthenticating(true);
      setMessage(null);
      api.post(TOTP_ENDPOINTS[step], { Ticket: totpTicket, Code: code })
         .then(response => {
            if (!response?.data?.success) {
               setMessage({ flavor: "alert-danger", text: response?.data?.message || TOTP_FAILURE_MESSAGE });
            } else if (step === TOTP_STEP.EMAIL_CODE) {
               // The setup response carries a new ticket for the confirm step; the old one is spent.
               setTotpTicket(response.data.ticket);
               setTotpSetup({ qrSvg: response.data.qrSvg, setupKey: response.data.setupKey });
               setTotpCode("");
               setTotpStep(TOTP_STEP.SETUP);
            } else {
               userCtx.signIn(response.data.user, response.data.user.token, response.data.menus);
            }
            setAuthenticating(false);
         })
         .catch((err) => {
            console.error(err);
            setMessage({ flavor: "alert-danger", text: err?.response?.data?.message || TOTP_FAILURE_MESSAGE });
            setAuthenticating(false);
         });
   }

   function beginTotpStep(step, ticket) {
      setTotpTicket(ticket);
      setTotpCode("");
      setTotpStep(step);
   }

   function getQueryStringVariable(variable) {
      if (!window.location.search) return null;
      try {
         var query = window.location.search.substring(1);
         // console.log('qs 0', query)
         var vars = query.split('&');
         for (var i = 0; i < vars.length; i++) {
            var pair = vars[i].split('=');
            // console.log('qs 1', pair)
            if (decodeURIComponent(pair[0]) === variable) {
               // console.log('qs 2', variable, decodeURIComponent(pair[1]))
               return decodeURIComponent(pair[1]);
            }
         }
      } catch (err) {
         console.error('failed to parse querystring', err);
         return null;
      }
   }

   function handleSubmit(e) {
      e.preventDefault();
      if (!username || username.length < 2 || !password || password.length < 7) {
         toast.error("Please ensure username is entered and at least 2 characters. Please ensure password is entered and at least 7 characters.");
         return;
      }
      // A handheld wedge scanner can append a badge scan onto the focused/autofilled username field. A real
      // username never holds a comma or the B1- token prefix, so either one means a badge landed here — send
      // the user to the badge-login screen to scan again rather than posting a malformed password attempt.
      // (The original scan is already consumed into the field, so a re-scan on the correct screen is needed.)
      if (username.includes(",") || username.toUpperCase().includes(badgeTokenPrefix)) {
         toast.info("Badge detected — please scan again.");
         navigate(scanLoginPath);
         return;
      }
      setAuthenticating(true);
      setMessage(null);
      api.post("Public/Authenticate", { Username: username, Password: password }).then((response) => {
         if (api.isRateLimited(response)) {
            // the api interceptor already toasted the server sentence
            setAuthenticating(false);
            return;
         }
         if (response.data.success) {
            storage.setItem("lastUsername", username);   // only persist on a SUCCESSFUL login, never on a failed/abandoned attempt
            if (response.data.redirectUrl) {
               if (response.data.redirectUrl === TOTP_REDIRECT.SIGN_IN) {
                  beginTotpStep(TOTP_STEP.SIGN_IN_CODE, response.data.ticket);
               } else if (response.data.redirectUrl === TOTP_REDIRECT.ENROLL_EMAIL) {
                  setTotpEmail(response.data.email);
                  setTotpMinutes(response.data.minutes);
                  setTotpNotice(response.data.notice || null);
                  beginTotpStep(TOTP_STEP.EMAIL_CODE, response.data.ticket);
               } else {
                  window.location = response.data.redirectUrl;
               }
            } else {
               userCtx.signIn(response.data.user, response.data.user.token, response.data.menus);
            }
         } else {
            setMessage({ flavor: "alert-danger", text: response?.data?.message })
         }
         // not using a finally here since the scanner browsers choke on it
         setAuthenticating(false);
      }).catch((err) => {
         console.error(err);
         setAuthenticating(false);
      });
   }

   function onTotpCodeKeyPress(e) {
      if (e.keyCode === 13) { // ENTER
         attemptTotpCode(e);
      }
   }

   // Re-runs the password step with the credentials still in state, which emails a fresh setup code and
   // issues a new ticket - both replace the ones held here.
   function resendTotpSetupCode() {
      if (authenticating) return;
      setAuthenticating(true);
      setMessage(null);
      api.post("Public/ResendTotpSetupCode", { Username: username, Password: password })
         .then((response) => {
            if (api.isRateLimited(response)) {
               // already toasted by the api interceptor; the busy state is cleared below
            } else if (response?.data?.success) {
               setTotpTicket(response.data.ticket);
               setTotpEmail(response.data.email);
               setTotpMinutes(response.data.minutes);
               setTotpNotice(null);
               setTotpCode("");
               setMessage({ flavor: "alert-success", text: "A new code has just been sent." });
            } else {
               setMessage({ flavor: "alert-danger", text: response?.data?.message || TOTP_RESEND_FAILURE_MESSAGE });
            }
            setAuthenticating(false);
         })
         .catch((err) => {
            console.error(err);
            setMessage({ flavor: "alert-danger", text: err?.response?.data?.message || TOTP_RESEND_FAILURE_MESSAGE });
            setAuthenticating(false);
         });
   }

   // Back to the username/password form - the way out when a ticket has expired.
   function resetTotpSteps() {
      setTotpStep(null);
      setTotpTicket(null);
      setTotpCode("");
      setTotpEmail("");
      setTotpMinutes(null);
      setTotpNotice(null);
      setTotpSetup(null);
      setMessage(null);
   }

   function resolveQueryStringParams() {
      // try {
      //   const queryParams = new URLSearchParams(search);
      //   console.log('qp', queryParams, getQueryStringVariable('state'), queryParams.has('state'), queryParams.get('state'))
      //   return {
      //     state: queryParams.has('state')
      //       ? queryParams.get('state')
      //       : null,
      //     code: queryParams.has('code')
      //       ? queryParams.get('code')
      //       : null
      //   };
      // } catch {
      return {
         state: getQueryStringVariable('state'),
         code: getQueryStringVariable('code')
      };
      // }
   }

   // Markup for the current authenticator-app step (a render helper, not a component).
   function renderTotpStep() {
      const isSetup = totpStep === TOTP_STEP.SETUP;
      const qrSvg = isSetup ? totpSetup?.qrSvg : null; // null when the server could not draw it - the typed key still works
      return (
         <div>
            {totpStep === TOTP_STEP.SIGN_IN_CODE &&
               <div>Enter the 6-digit code shown for ServiceThread in your authenticator app.</div>
            }
            {totpStep === TOTP_STEP.EMAIL_CODE && (totpNotice
               ? <div>{totpNotice}</div>
               : <div>
                  We've sent a 6-digit code to {totpEmail}. Enter it below.
                  {totpMinutes ? ` The code is good for ${totpMinutes} minutes.` : ""}
               </div>)
            }
            {isSetup &&
               <div>
                  {qrSvg &&
                     <div>
                        <div>Open your authenticator app, choose Add account (or +), and scan this code.</div>
                        <img
                           className="d-block my-2"
                           src={"data:image/svg+xml;charset=utf-8," + encodeURIComponent(qrSvg)}
                           alt="QR code for setting up an authenticator app"
                           style={{ width: TOTP_QR_SIZE_PX, height: TOTP_QR_SIZE_PX, backgroundColor: "white" }}
                        />
                     </div>
                  }
                  <div>Can't scan it? Choose Enter a setup key in the app and type:</div>
                  <code className="d-block my-2 user-select-all" style={{ fontSize: "14pt" }}>
                     {totpSetup?.setupKey}
                  </code>
                  <div>Then enter the 6-digit code the app shows for ServiceThread.</div>
               </div>
            }
            <div className="d-flex mt-2">
               {/* keyed by step so each step mounts a fresh input and autoFocus lands in it */}
               <Input
                  key={totpStep}
                  type="text"
                  className="form-control"
                  name="totpCode"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  style={{ width: "180px", fontSize: "20pt", marginRight: "10px" }}
                  onChange={(e) => setTotpCode(e.target.value)}
                  onKeyDown={onTotpCodeKeyPress}
                  value={totpCode}
                  placeholder="6-digit code"
               />
               {/* One small spinner in the button's place, as the password form swaps its Sign In button; the links
                   below stay disabled meanwhile, so a second spinner would only repeat it. */}
               <ServiceThreadSpinner on={authenticating} small>
                  <Button
                     disabled={authenticating || stripWhitespace(totpCode).length < TOTP_CODE_LENGTH}
                     color="primary"
                     style={{ fontSize: "18pt" }}
                     onClick={(e) => attemptTotpCode(e)}
                  >
                     OK
                  </Button>
               </ServiceThreadSpinner>
            </div>
            <div>
               {totpStep === TOTP_STEP.EMAIL_CODE &&
                  <Button color={"link"} disabled={authenticating} onClick={resendTotpSetupCode}>
                     Resend code
                  </Button>
               }
               <Button color={"link"} disabled={authenticating} onClick={resetTotpSteps}>
                  Start over
               </Button>
            </div>
         </div>
      );
   }

   return (
      <DesktopContainer fluid className="pt-5">
         <Row className="pt-5 justify-content-center position-static">
            <Col sm={12} md={6}>
               <Row className="py-3 justify-content-center">
                  <Col className="text-center">
                     <img id="Logo" src={Logo} alt="logo" />
                  </Col>
               </Row>
               <Row className="mt-3">
                  <Col xs="12">
                     {totpStep
                        ? renderTotpStep()
                        :
                        <div>
                           {hasScanner &&
                              <div>
                                 <Row>
                                    <Link
                                       state={{ email: username }}
                                       to={scanLoginPath}
                                       className={"btn w-100 mobileAnchor"}
                                    >
                                       Scan QR Code to Login
                                    </Link>
                                 </Row>
                                 <Row className="lineheight15">&nbsp;</Row>
                              </div>
                           }

                           <Form id="sign-in-form" onSubmit={handleSubmit}>
                           <FormGroup>
                              <Label>Username</Label>
                              <Input
                                 className="form-control"
                                 autoFocus
                                 type="text"
                                 value={username}
                                 onChange={(e) => setUserName(e.target.value)}
                                 placeholder="Enter username"
                              />
                           </FormGroup>
                           <FormGroup>
                              <Label>Password</Label>
                              <InputGroup>
                                 <Input
                                    className="form-control"
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter password"
                                 />
                                 <Button
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{ width: "3em" }}
                                 >
                                    <FontAwesomeIcon
                                       icon={showPassword ? "eye-slash" : "eye"}
                                    />
                                 </Button>
                              </InputGroup>
                           </FormGroup>
                           <ServiceThreadSpinner on={authenticating}>
                              <FormGroup>
                                 <Button
                                    type="submit"
                                    className={"float-start"}
                                    color={"primary"}
                                 >
                                    Sign In
                                 </Button>
                                 <Link
                                    state={{ email: username }}
                                    to={forgotPasswordPath}
                                    className="float-end"
                                 >
                                    Forgot Password
                                 </Link>
                              </FormGroup>
                           </ServiceThreadSpinner>
                           </Form>
                        </div>                        
                     }
                  </Col>
               </Row>
               {message ? (
                  <Row className="mt-3">
                     <Col>
                        <RSAlert className={message.flavor}>{message.text}</RSAlert>
                     </Col>
                  </Row>
               ) : null}
            </Col>
         </Row>
      </DesktopContainer>
   );
}
